import {html} from 'lit';
import {companyLang} from '#locale/translations/company.js';
import {SupportedLocales} from '#lit/types/locale.js';
import {locale} from '#lit/common/locale/index.js';

export const abLocale = {
  [SupportedLocales.EN]: {
    audienceTags: {
      addTags: 'Add tags',
      emptySearchResultsMessage: `There are no tags available for your team.
        Create your first tag by typing above and pressing enter.`,
      noSearchResultsMessage: 'There are no tags that match your query.',
      audienceLoading: 'Audience data is loading...',
    },
    filterMenuItem: {
      name: 'Filter',
      description: 'Filter your customer, transaction, and event data.',
    },
    customerFilterMenuItem: {
      name: 'Customer Filter',
      description:
        'Target customers using fields belonging to your user dataset.',
    },
    transactionFilterMenuItem: {
      name: 'Transaction Filter',
      description:
        'Target your customers using fields from your transaction data.',
    },
    eventFilterMenuItem: {
      name: 'Event Filter',
      description: 'Target your customers using fields from your events data.',
    },
    flexMenuItem: {
      name: 'Dataset',
      description:
        'Filter customers by transactions, events, etc. by adding or activating a dataset.',
    },
    googleAdMenuItem: {
      name: 'Google Search Ad',
      description:
        'Using Typeface AI create a Google Ad with a couple of short prompts.',
    },
    googleDisplayAdMenuItem: {
      name: 'Google Display Ad',
      description:
        'Using Typeface AI create a Google Display Ad with A.I. generated images and text.',
    },
    typeFaceEmail: {
      name: 'Email',
      description:
        'Using Typeface AI create an email with A.I. generated content.',
    },
    typeFaceTwitterPost: {
      name: 'Twitter Post',
      description:
        'Using Typeface AI create a Twitter Post with just a few prompts.',
    },
    typeFaceBlogPost: {
      name: 'Blog Post',
      description:
        'Create a blog post in seconds with generated content using Typeface AI.',
    },
    audienceMenuItem: {
      name: 'Audience',
      description: 'Include or Exclude customers from a pre-existing audience.',
    },
    textMenuItem: {
      name: 'Text',
      description: 'Add content to help describe your audience.',
    },
    headingMenuItem: {
      name: 'Heading',
      description: 'Organize your audience into titled sections.',
    },
    sectionMenuItem: {
      name: 'Section',
      description:
        'Add another section to your audience for adding filters, content, and more.',
    },
    inspector: {
      lastUpdatedLabel: 'Last refreshed',
      revenueLabel: 'Total Revenue',
    },
    nodeMenu: {
      sectionLock: {
        summary:
          'This section is locked. To add another kind of node, you will need to unlock this section.',
        full: 'Sections partition your audience by letting you add filters, datasets, and other types of nodes to a new part of the builder. All nodes added using a section are placed above the section menu, thereby separating nodes into logical groups. Whenever a section is locked, this means only nodes of a single type (e.g. filters) are permitted in the section. Unlocking a section restores the ability to add nodes of any type.',
      },
    },
    expressionMultiselect: {
      fetchErrorMessage:
        'There was an error fetching field values from your data warehouse.',
    },
    templates: {
      blank: {
        heading: 'Build from scratch',
        description:
          'Begin building your audience from a blank slate. For experienced users only.',
      },
      default: {
        heading: 'Start with the basics',
        description:
          'Everything you need to begin segmenting your customer data.',
      },
      aiStudio: {
        heading: 'Build with AI Studio',
        description:
          'Use natural language to define and build an audience, just like you would in a marketing brief.',
      },
    },
    breakdownReports: {
      title: 'Breakdowns',
      refreshCopy: 'Refresh breakdowns to see latest values',
      description:
        'Add granular reports breaking down customers matching this audience using fields from your data warehouse.',
      additionLabel: 'Add breakdown',
      menu: {
        makeDefaultLabel: 'Set as default for dataset group',
        removeDefaultLabel: 'Remove as default for dataset group',
        deleteLabel: 'Delete',
      },
      lastUpdatedLabel: 'Last refreshed',
      getNullMessageTemplate: (nullCount: string, nullPercentage: string) => {
        return html`<div class="breakdown__report__null-count">
          Note: ${nullCount} (${nullPercentage}%) are Null.
        </div>`;
      },
    },
    comparisons: {
      label: 'Audiences',
      initialDescription:
        'Find a pre-existing audience below to compare overlap with the customers matching your current criteria.',
      audienceSearch: {
        separatorTitle: 'Add More Audiences',
        tableColumnName: 'Name',
      },
      topAudiences: {
        label: 'Top Overlap Audiences',
      },
    },
    marketplace: {
      goBack: 'Back to All Recipe',
      missingAssociations: "Looks like you're missing a few required labels.",
    },
    csvMessages: {
      largeCSVHeader: 'Preparing your CSV',
      largeCSVText:
        'Building your CSV file, please check your email in a few minutes.',
      defaultHeader: 'Downloading your CSV',
      defaultTextPrimary: 'Building your CSV file, please wait.',
      defaultTextSecondary:
        'You may close this modal but please do not navigate away from this page until the download is complete.',
      largeAudienceExportHeader: 'Large Audience Exports',
      largeAudienceExportText:
        "Your audience is larger then 500K records. For larger exports, we'll send you the CSV file to the email address associated with your Growthloop account. This can take several minutes depending on the size of your audience.",
      additionalFieldsHeader: 'Additional Fields',
      additionalFieldsText:
        'Add additional fields that you would like to export to your CSV file',
      successfulDownload: 'CSV was successfully downloaded',
    },
    exportLabels: {
      createExport: 'Create Export',
      csvDownload: 'CSV Download',
    },
    errorMessages: {
      fullSchema: `Error getting schema from your Datawarehouse. Don't worry, our team has been notified and will reach out as soon as possible.
Getting your audiences activated is our top priority.
You can also get in touch quickly at ${companyLang[locale.current].supportEmail}`,
      insights: 'Error performing audience query: ',
    },
    modalMenu: {
      filterLabel: 'Attributes',
      audienceLabel: 'Audiences',
      datasetLabel: 'Datasets',
      contentLabel: 'Content',
      filterDescription: 'Target users based on how they match your data.',
      audienceDescription: 'Include or exclude existing audiences.',
      datasetDescription:
        'Filter on data contained in other dataset belonging to your schema.',
      contentDescription: 'Annotate your audience with custom content.',
    },
    exportGallery: {
      newDestination: 'New Destination',
      addNewDestination: 'Add New Destination',
      disabledTooltip: {
        label: 'Why is this disabled?',
        disabledContent: (destinationName: string) =>
          `You’ve already exported this audience to ${destinationName}. To rerun the export as is, click 'Run' on the Exports tab. To update the export settings, click 'Manage' on the Exports tab.`,
        tooLargeContent:
          'This audience is too large to download as CSV. You can still export it to a bucket or data warehouse destination',
      },
    },
  },
  [SupportedLocales.ES]: {
    audienceTags: {
      addTags: 'Agregar etiquetas',
      emptySearchResultsMessage: `No hay etiquetas disponibles para tu equipo.
        Crea tu primera etiqueta escribiendo arriba y presionando enter.`,
      noSearchResultsMessage: 'No hay etiquetas que coincidan con tu búsqueda.',
      audienceLoading: 'Cargando datos de audiencia...',
    },
    filterMenuItem: {
      name: 'Filtrar',
      description: 'Filtra tus datos de clientes, transacciones y eventos.',
    },
    customerFilterMenuItem: {
      name: 'Filtro de Clientes',
      description:
        'Segmenta clientes usando campos pertenecientes a tu conjunto de datos de usuarios.',
    },
    transactionFilterMenuItem: {
      name: 'Filtro de Transacciones',
      description:
        'Segmenta tus clientes usando campos de tus datos de transacciones.',
    },
    eventFilterMenuItem: {
      name: 'Filtro de Eventos',
      description:
        'Segmenta tus clientes usando campos de tus datos de eventos.',
    },
    // FIXME(CHARLIE) - Is this translation correct?
    csvMessages: {
      largeCSVHeader: 'Preparando tu CSV',
      largeCSVText:
        'Construyendo tu archivo CSV, por favor revisa tu correo electrónico en unos minutos.',
      defaultHeader: 'Descargando tu CSV',
      defaultTextPrimary: 'Construyendo tu archivo CSV, por favor espera.',
      defaultTextSecondary:
        'Puedes cerrar este modal, pero por favor no navegues fuera de esta página hasta que la descarga esté completa.',
      largeAudienceExportHeader: 'Exportaciones de Audiencia Grande',
      largeAudienceExportText:
        'Tu audiencia es mayor de 500K registros. Para exportaciones más grandes, te enviaremos el archivo CSV a la dirección de correo electrónico asociada con tu cuenta de Growthloop. Esto puede tardar varios minutos dependiendo del tamaño de tu audiencia.',
      additionalFieldsHeader: 'Campos Adicionales',
      additionalFieldsText:
        'Añade campos adicionales que te gustaría exportar a tu archivo CSV',
      successfulDownload: 'El archivo CSV se ha descargado con éxito',
    },
    exportLabels: {
      createExport: 'Crear Exportación',
      csvDownload: 'Descargar CSV',
    },
    flexMenuItem: {
      name: 'Conjunto de Datos',
      description:
        'Filtra clientes por transacciones, eventos, etc. agregando o activando un conjunto de datos.',
    },
    googleAdMenuItem: {
      name: 'Anuncio de Búsqueda en Google',
      description:
        'Usa Typeface AI para crear un anuncio en Google con un par de indicaciones cortas.',
    },
    googleDisplayAdMenuItem: {
      name: 'Anuncio de Display en Google',
      description:
        'Usa Typeface AI para crear un anuncio de display en Google con imágenes y texto generados por IA.',
    },
    typeFaceEmail: {
      name: 'Correo Electrónico',
      description:
        'Usa Typeface AI para crear un correo electrónico con contenido generado por IA.',
    },
    typeFaceTwitterPost: {
      name: 'Publicación en Twitter',
      description:
        'Usa Typeface AI para crear una publicación en Twitter con solo unas pocas indicaciones.',
    },
    typeFaceBlogPost: {
      name: 'Publicación de Blog',
      description:
        'Crea una publicación de blog en segundos con contenido generado usando Typeface AI.',
    },
    audienceMenuItem: {
      name: 'Audiencia',
      description: 'Incluye o excluye clientes de una audiencia preexistente.',
    },
    textMenuItem: {
      name: 'Texto',
      description: 'Agrega contenido para ayudar a describir tu audiencia.',
    },
    headingMenuItem: {
      name: 'Encabezado',
      description: 'Organiza tu audiencia en secciones tituladas.',
    },
    sectionMenuItem: {
      name: 'Sección',
      description:
        'Agrega otra sección a tu audiencia para agregar filtros, contenido y más.',
    },
    inspector: {
      lastUpdatedLabel: 'Última actualización',
      revenueLabel: 'Ingresos Totales',
    },
    nodeMenu: {
      sectionLock: {
        summary:
          'Esta sección está bloqueada. Para agregar otro tipo de nodo, deberás desbloquear esta sección.',
        full: 'Las secciones dividen tu audiencia permitiéndote agregar filtros, conjuntos de datos y otros tipos de nodos a una nueva parte del constructor. Todos los nodos agregados usando una sección se colocan por encima del menú de la sección, separando así los nodos en grupos lógicos. Siempre que una sección esté bloqueada, esto significa que solo se permiten nodos de un solo tipo (por ejemplo, filtros) en la sección. Desbloquear una sección restaura la capacidad de agregar nodos de cualquier tipo.',
      },
    },
    expressionMultiselect: {
      fetchErrorMessage:
        'Hubo un error al recuperar los valores de los campos de tu almacén de datos.',
    },
    templates: {
      blank: {
        heading: 'Construir desde cero',
        description:
          'Comienza a construir tu audiencia desde una hoja en blanco. Solo para usuarios experimentados.',
      },
      default: {
        heading: 'Comienza con lo básico',
        description:
          'Todo lo que necesitas para comenzar a segmentar los datos de tus clientes.',
      },
      marve: {
        heading: 'Construir con Marve',
        description:
          'Usa lenguaje natural para definir y construir una audiencia, tal como lo harías en un informe de marketing.',
      },
    },
    breakdownReports: {
      title: 'Desgloses',
      refreshCopy: 'Actualiza los desgloses para ver los valores más recientes',
      description:
        'Agrega informes granulares desglosando clientes que coinciden con esta audiencia usando campos de tu almacén de datos.',
      additionLabel: 'Agregar desglose',
      menu: {
        makeDefaultLabel:
          'Establecer como predeterminado para el grupo de conjuntos de datos',
        removeDefaultLabel:
          'Eliminar como predeterminado para el grupo de conjuntos de datos',
        deleteLabel: 'Eliminar',
      },
      lastUpdatedLabel: 'Última actualización',
      getNullMessageTemplate: (nullCount: string, nullPercentage: string) => {
        return html`<div class="breakdown__report__null-count">
          Nota: ${nullCount} (${nullPercentage}%) son Nulos.
        </div>`;
      },
    },
    comparisons: {
      label: 'Audiencias',
      initialDescription:
        'Encuentra una audiencia preexistente a continuación para comparar la superposición con los clientes que coinciden con tus criterios actuales.',
      audienceSearch: {
        separatorTitle: 'Agregar Más Audiencias',
        tableColumnName: 'Nombre',
      },
      topAudiences: {
        label: 'Audiencias de Mayor Superposición',
      },
    },
    marketplace: {
      goBack: 'Volver a Todas las Recetas',
      missingAssociations: 'Parece que te faltan algunas etiquetas requeridas.',
    },
    errorMessages: {
      fullSchema: `Error al obtener el esquema de tu almacén de datos. No te preocupes, nuestro equipo ha sido notificado y se pondrá en contacto lo antes posible.
Activar tus audiencias es nuestra máxima prioridad.
También puedes ponerte en contacto rápidamente en ${companyLang[locale.current].supportEmail}`,
      insights: 'Error al realizar la consulta de la audiencia: ',
    },
    modalMenu: {
      filterLabel: 'Atributos',
      audienceLabel: 'Audiencias',
      datasetLabel: 'Conjuntos de datos',
      contentLabel: 'Contenido',
      filterDescription:
        'Dirige a los usuarios en función de cómo se ajustan a tus datos.',
      audienceDescription: 'Incluye o excluye audiencias existentes.',
      datasetDescription:
        'Filtra datos contenidos en otros conjuntos de datos que pertenecen a tu esquema.',
      contentDescription: 'Anota tu audiencia con contenido personalizado.',
    },
    exportGallery: {
      newDestination: 'Nuevo Destino',
      addNewDestination: 'Añadir Nuevo Destino',
      disabledTooltip: {
        label: '¿Por qué está deshabilitado?',
        disabledContent: (destinationName: string) =>
          `Ya has exportado esta audiencia a ${destinationName}. Para volver a ejecutar la exportación tal cual, haz clic en 'Ejecutar' en la pestaña Exportaciones. Para actualizar la configuración de la exportación, haz clic en 'Administrar' en la pestaña Exportaciones.`,
        tooLargeContent:
          'Esta audiencia es demasiado grande para descargarla como CSV. Aún puedes exportarla a un destino de bucket o almacén de datos',
      },
    },
  },
};
